import React, { useEffect, useRef, useState } from "react";
import Navbar from "../../components/Navbar";
import Styles from "../../styles/dashboard/dashboard.module.scss";
import { adminRoutes } from "../../routers/AdminRouters";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Close_Button from "../../assets/auth/close.png";
import { getGym, getScheduleDetails, getScheduleGym } from "../../services";
import Swal from "sweetalert2";
import { MdArrowForwardIos, MdArrowBackIos } from "react-icons/md";
import InfiniteScroll from "react-infinite-scroll-component";
import { setScheduleGymState } from "../../redux";
import { FaSearch } from "react-icons/fa";

function secondsToHhmmss(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((unit) => String(unit).padStart(2, "0")) // Ensures 2-digit format (e.g., 01, 09)
    .join(":");
}
const capitalizeEachWord = (string) => {
  if (!string) return ""; // Handle empty or undefined strings
  return string
    .split(" ") // Split into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(" "); // Join back into a string
};
const LIMIT = 15;

const Dashboard = () => {
  const navigate = useNavigate();
  const nameRef = useRef();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const { gyms } = useSelector((state) => state.gym);
  const { schedules } = useSelector((state) => state.gym);
  const { sessionsSchedule } = useSelector((state) => state.sessions);
  const [selectedGymId, setSelectedGymId] = useState(null);
  const [displayedSessionsSchedule, setDisplayedSessionsSchedules] = useState(
    []
  );
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("latest");
  const fetchSessionScheduled = async (
    pageNum = page,
    query = "",
    sort = sortOrder
  ) => {
    if (loading) return;

    try {
      setLoading(true);
      console.log("Fetching sessions for page:", page);

      const newSessionsSchedules = await getScheduleDetails(
        false,
        pageNum,
        LIMIT,
        query,
        sort
      );

      if (
        newSessionsSchedules &&
        Array.isArray(newSessionsSchedules) &&
        newSessionsSchedules.length > 0
      ) {
        setDisplayedSessionsSchedules((prev) => [
          ...prev,
          ...newSessionsSchedules,
        ]);
        setPage((prev) => prev + 1);
        console.log("Fetched new sessions:", newSessionsSchedules.length);
      } else {
        console.log("No more sessions to fetch");
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGym();
    fetchSessionScheduled();
  }, []);

  const handleSearch = async (e, sort = sortOrder) => {
    const query = e.target.value;
    setSearchQuery(query);

    try {
      if (query.trim() === "") {
        setDisplayedSessionsSchedules([]);
        setPage(1);
        setHasMore(true);
        setTimeout(() => {
          fetchSessionScheduled(1, "", sort);
        }, 10);
      } else {
        const newSessions = await getScheduleDetails(false, 1, -1, query, sort);
        if (Array.isArray(newSessions)) {
          setDisplayedSessionsSchedules(newSessions);
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  const createSession = () => {
    navigate(adminRoutes.createSession);
  };
  const assignSession = () => {
    navigate(adminRoutes.listGym);
  };

  const createGym = () => {
    navigate(adminRoutes.createGym);
  };

  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 (Sunday) to 6 (Saturday)
    const daysInMonth = new Date(year, month + 1, 0).getDate(); // Total days in the current month
    const daysInPrevMonth = new Date(year, month, 0).getDate(); // Total days in the previous month

    const daysArray = [];

    // Add previous month's last few days
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      daysArray.push({
        day: daysInPrevMonth - i,
        isPrevMonth: true,
      });
    }

    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push({
        day: i,
        isCurrentMonth: true,
      });
    }

    // Add next month's first few days to complete the last row
    while (daysArray.length % 7 !== 0) {
      daysArray.push({
        day: daysArray.length - daysInMonth - firstDayOfMonth + 1,
        isNextMonth: true,
      });
    }

    return daysArray;
  };

  const handleApi = async (event) => {
    const gymId = event.target.value;
    setSelectedGymId(gymId);
    await getScheduleGym(false, gymId);
  };

  useEffect(() => {
    if (selectedGymId) {
      setCurrentDate(new Date());
    }
  }, [selectedGymId, schedules]);

  const handleDateClick = (date) => {
    if (date instanceof Date && !isNaN(date.getTime())) {
      setSelectedDate(date);
    } else {
      console.error("Invalid date selected", date);
    }
  };

  const getWeekNumber = (dateString) => {
    const date = new Date(dateString);

    // Set the date to Thursday of the current week (to align with ISO 8601)
    const targetDate = new Date(date);
    targetDate.setDate(date.getDate() - ((date.getDay() + 6) % 7) + 3);

    // Get the first Thursday of the year
    const firstThursday = new Date(targetDate.getFullYear(), 0, 4);
    firstThursday.setDate(
      firstThursday.getDate() - ((firstThursday.getDay() + 6) % 7)
    );

    // Calculate the difference in days and determine the week number
    const weekNumber =
      Math.round((targetDate - firstThursday) / 86400000 / 7) + 1;

    return weekNumber;
  };

  const showSubFormatsPopup = (scheduledGyms) => {
    const closeButtonStyles = `
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    outline: none;
    width: 40px;
    height: 40px;

`;

    const containerStyles = `
    position: relative;
    padding: 0;
    background-color: #ffffff;
    border-radius: 8px;
    overflow: hidden;
    margin: 0;
    padding: 0; 
    border: none; 
`;

    const headerStyles = `
    background: radial-gradient(798.09% 100% at 0% 49.32%, #E27908 0%, #7C4205 100%);
    color: #F9FBFC;
    text-align: left;
    outline: 4px solid #21211C;
    font-family: vanguard_cfdemi_bold;
    padding: 20px 20px;
    font-size: 22px;
    letter-spacing: 1.1px;
    text-transform: uppercase;
    margin: 0;
    border: none;
`;

    const gymPopupHTML = `
    <div style="${containerStyles}">
      <!-- Header with Close Button -->
      <div style="${headerStyles}">
        SCHEDULED GYMS
        <button style="${closeButtonStyles}" id="customCloseButton">
          <img src="${Close_Button}" alt="Close" style="width: 40px; height: 40px; border: none; outline: none;">
        </button>
      </div>

      <!-- Gym List (Scrollable) -->
      <div style="max-height: 300px; overflow-y: auto; padding-left: 10px;padding-right: 10px;padding-top: 11px;padding-bottom: 20px; display: flex; flex-wrap: wrap; gap: 10px; justify-content: center;">
        ${scheduledGyms
          .map(
            (gym) =>
              `<span style="
              padding: 7px 10px;
               border: 1px solid #808080;
                border-radius: 20px;
                background-color: #fff;
                font-size: 14px;
                font-family: gotham_bookregular;
                display: inline-block;font-family: 'gotham_bookregular';font-size: 0.85rem;
              ">${gym.name}</span>`
          )
          .join("")}
      </div>

      <!-- Close Button -->
    
      <div style="text-align: center; padding-top: 20px;padding-bottom:20px;border-top: 1px solid #B8B8B8;">

      <button 
          id="closeButton" 
          style="background:transparent;
          color: #21211C; padding: 10px 50px;
          font-family: gothambold;
          font-size: 14px;
          border-radius: 8px; cursor: pointer;border: 1px solid #F79328;height: 40px;display: inline-flex;">
          Close
        </button>
      </div>
    </div>
`;

    Swal.fire({
      html: gymPopupHTML,
      showConfirmButton: false,
      width: "900px",
      background: "none",
      backdrop: true,
      customClass: {
        popup: "no-padding-modal",
      },
      didOpen: () => {
        document.getElementById("closeButton").addEventListener("click", () => {
          Swal.close();
        });

        document
          .getElementById("customCloseButton")
          .addEventListener("click", () => {
            Swal.close();
          });
      },
    });
  };

  return (
    <div>
      <Navbar />
      <div className={Styles.dashboard_list_container_main}>
        <div className={Styles.dashboard_list_container}>
          <div className={Styles.dashboard_top_bar}>
            <div className={Styles.dashboard_top_bar_flex}>
              <div
                onClick={createSession}
                className={Styles.dashboard_top_bar_flex_child}
              >
                <h1>Create New Session</h1>
              </div>

              <div
                onClick={assignSession}
                className={Styles.dashboard_top_bar_flex_child}
              >
                <h1>Assign Session</h1>
              </div>

              <div
                onClick={createGym}
                className={Styles.dashboard_top_bar_flex_child}
              >
                <h1>Create New Gym</h1>
              </div>
            </div>
          </div>
        </div>

        <div className={Styles.dashboard_session_list_main}>
          <div className={Styles.dashboard_session_list_child_table}>
            <div className={Styles.dashboard_session_list_child_table_head}>
              <h1>Session List</h1>

              <div className={Styles.rightControls}>
                <select
                  className={Styles.sortselect}
                  value={sortOrder}
                  onChange={(e) => {
                    const selected = e.target.value;
                    setSortOrder(selected);
                    setDisplayedSessionsSchedules([]);
                    setPage(1);
                    setHasMore(true);

                    if (searchQuery.trim()) {
                      handleSearch(
                        { target: { value: searchQuery } },
                        selected
                      );
                    } else {
                      fetchSessionScheduled(1, searchQuery, selected);
                    }
                  }}
                >
                  <option value="latest">Latest Sessions</option>
                  <option value="oldest">Oldest Sessions</option>
                  <option value="asc">A - Z (Sort in ascending order)</option>
                  <option value="desc">Z - A (Sort in descending order)</option>
                </select>

                <div
                  className={`${Styles.searchContainerchildtwo} ${
                    searchQuery ? Styles.active : ""
                  }`}
                >
                  <FaSearch className={Styles.searchIcon} />
                  <input
                    type="text"
                    className={Styles.searchInput}
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                </div>
              </div>
            </div>

            <div className={Styles.tableFixedHeader}>
              <div className={Styles.scrollableWrapper}>
                <table className={Styles.session_table}>
                  <thead>
                    <tr>
                      <th>Session Name</th>
                      <th>Formats</th>
                      <th>Time</th>
                      <th>Scheduled Gyms</th>
                    </tr>
                  </thead>
                </table>

                <div id="scrollableDiv" className={Styles.scrollableContainer}>
                  {!displayedSessionsSchedule ||
                  displayedSessionsSchedule.length === 0 ? (
                    <div className={Styles.no_sessions_container}>
                      <p
                        style={{
                          textAlign: "center",
                          padding: "10px",
                          color: "white",
                          fontSize: "16px",
                        }}
                      >
                        No sessions available
                      </p>
                    </div>
                  ) : (
                    <InfiniteScroll
                      dataLength={displayedSessionsSchedule.length}
                      next={fetchSessionScheduled}
                      hasMore={hasMore}
                      loader={
                        <>
                        </>
                      }
                      endMessage={
                        <p
                          style={{
                            textAlign: "center",
                            padding: "10px",
                            color: "#f3f3f6",
                            fontFamily: "gotham_bookregular",

                          }}
                        >
                          {" "}
                        </p>
                      }
                      scrollableTarget="scrollableDiv"
                      scrollThreshold={0.8}
                    >
                      <table className={Styles.session_table}>
                        <tbody>
                          {displayedSessionsSchedule?.map((session, index) => (
                            <tr key={session._id}>
                              <td>
                                {session?.name
                                  ? capitalizeEachWord(session.name)
                                  : "N/A"}
                              </td>
                              <td>
                                {session.formats &&
                                session.formats.length > 0 ? (
                                  session.formats.length > 2 ? (
                                    <>
                                      {session.formats
                                        .slice(0, 2)
                                        .map((formatItem, idx) => (
                                          <span
                                            key={idx}
                                            className={Styles.format_badge}
                                          >
                                            {formatItem.format?.name || "N/A"}
                                          </span>
                                        ))}
                                      <span className={Styles.format_badge}>
                                        Other
                                      </span>
                                    </>
                                  ) : (
                                    session.formats.map((formatItem, idx) => (
                                      <span
                                        key={idx}
                                        className={Styles.format_badge}
                                      >
                                        {formatItem.format?.name || "N/A"}
                                      </span>
                                    ))
                                  )
                                ) : (
                                  <span className={Styles.format_badge}>
                                    N/A
                                  </span>
                                )}
                              </td>
                              <td>
                                {secondsToHhmmss(session.duration) || "N/A"}
                              </td>
                              <td>
                                {session.scheduledGyms &&
                                session.scheduledGyms.length > 0 ? (
                                  session.scheduledGyms.length > 2 ? (
                                    <>
                                      {session.scheduledGyms
                                        .slice(0, 2)
                                        .map((sub, idx) => (
                                          <span
                                            key={sub._id}
                                            className={Styles.gym_badge}
                                          >
                                            {sub.name}
                                          </span>
                                        ))}
                                      <span
                                        className={Styles.gym_badge_no}
                                        onClick={() =>
                                          showSubFormatsPopup(
                                            session.scheduledGyms
                                          )
                                        }
                                      >
                                        +{session.scheduledGyms.length - 2}
                                      </span>
                                    </>
                                  ) : (
                                    session.scheduledGyms.map((sub, idx) => (
                                      <span
                                        key={sub._id}
                                        className={Styles.gym_badge}
                                      >
                                        {sub.name}
                                      </span>
                                    ))
                                  )
                                ) : (
                                  <span className={Styles.format_badge}>
                                    N/A
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </InfiniteScroll>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={Styles.dashboard_session_list_child_calander}>
            <div className={Styles.input_group}>
              <select onChange={handleApi} ref={nameRef}>
                <option value="" disabled selected>
                  Choose from list
                </option>
                {gyms.map((gym) => (
                  <option key={gym._id} value={gym._id}>
                    {gym.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={Styles.calendar_container}>
              <div className={Styles.calendar_header}>
                <button onClick={prevMonth}>
                  <MdArrowBackIos />
                </button>
                <h3>
                  {currentDate.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </h3>
                <button onClick={nextMonth}>
                  <MdArrowForwardIos />
                </button>
                {/* <button className={Styles.today_btn} onClick={goToCurrentMonth}>
                  Today
                </button> */}
              </div>
              <div className={Styles.calendar_day_labels}>
                {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map(
                  (day, index) => (
                    <div key={index} className={Styles.day_label}>
                      {day}
                    </div>
                  )
                )}
              </div>
              <div className={Styles.calendar_grid}>
                {getDaysInMonth().map((dayObj, index) => {
                  const { day, isPrevMonth, isCurrentMonth, isNextMonth } =
                    dayObj;

                  const dateInstance = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth() +
                      (isPrevMonth ? -1 : isNextMonth ? 1 : 0),
                    day
                  );
                  const formattedDate = dateInstance
                    .toISOString()
                    .split("T")[0];

                  const today = new Date();

                  today.setHours(0, 0, 0, 0);
                  const isPast = dateInstance < today;

                  const isScheduled = schedules.some((schedule) =>
                    schedule.date.startsWith(formattedDate)
                  );

                  const cellClass = isPrevMonth
                    ? Styles.prev_month_day
                    : isNextMonth
                    ? Styles.next_month_day
                    : isPast
                    ? Styles.past_day
                    : isScheduled
                    ? Styles.scheduled_day
                    : Styles.future_day;

                  return (
                    <div
                      key={index}
                      className={`${Styles.calendar_cell} ${cellClass} ${
                        selectedDate &&
                        selectedDate.getDate() === dateInstance.getDate() &&
                        selectedDate.getMonth() === dateInstance.getMonth()
                          ? Styles.selected_day
                          : ""
                      }`}
                      onClick={() => {
                        if (!isPast) {
                          handleDateClick(dateInstance);
                        }
                      }}
                      style={{
                        cursor: isPast ? "not-allowed" : "pointer",
                        opacity: isPast ? 0.5 : 1,
                        pointerEvents: isPast ? "none" : "auto",
                      }}
                    >
                      <span className={Styles.day_number}>{day}</span>
                    </div>
                  );
                })}
              </div>

              <div className={Styles.calender_notify_boxs}>
                <div className={Styles.calender_box_child_one}>
                  <button disabled></button>
                  <span>Selected</span>
                </div>

                <div className={Styles.calender_box_child_two}>
                  <button disabled></button> <span>Scheduled</span>
                </div>

                <div className={Styles.calender_box_child_three}>
                  <button disabled></button> <span>Completed</span>
                </div>
              </div>
            </div>
            {selectedDate && (
              <div className={Styles.schedule_details}>
                <h3>SCHEDULE DETAILS</h3>
                <hr />
                {schedules.filter(
                  (schedule) =>
                    schedule.date.startsWith(
                      selectedDate.toISOString().split("T")[0]
                    ) && schedule.session
                ).length > 0 ? (
                  [
                    ...new Map(
                      schedules
                        .filter(
                          (schedule) =>
                            schedule.date.startsWith(
                              new Date(
                                currentDate.getFullYear(),
                                currentDate.getMonth(),
                                selectedDate.getDate()
                              )
                                .toISOString()
                                .split("T")[0]
                            ) && schedule.session
                        )
                        .map((schedule) => [schedule.session.code, schedule])
                    ).values(),
                  ]
                    .slice(0, 1)
                    .map((schedule) => (
                      <div key={schedule._id} className={Styles.schedule_item}>
                        <p>
                          <strong>Session Name</strong> {schedule.session.name}
                        </p>
                        <hr />
                        <p>
                          <strong>Session Duration</strong>{" "}
                          {secondsToHhmmss(schedule.session.duration)}
                        </p>
                        <hr />
                        <p>
                          <strong>No Formats</strong>{" "}
                          {schedule.session.formats.length}
                        </p>
                        <hr />
                        <p>
                          <strong>Week</strong> {getWeekNumber(schedule.date)}
                        </p>
                        <hr />
                      </div>
                    ))
                ) : (
                  <p>No session found</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
